import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { inPerformanceResources, inPerformanceNamespaces } from './inPerformance/locales/resources';
import { useLanguageTransitionStore } from './zustand/language-transition-store';

export const LANGUAGE_STORAGE_KEY = 'language';

const SUPPORTED_LANGUAGES = ['en', 'ar'];

function normalizeLanguageCode(lng) {
  if (!lng || typeof lng !== 'string') {
    return 'en';
  }
  const code = lng.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(code) ? code : 'en';
}

/** Read persisted UI language; falls back to `en` if missing or unsupported. */
export function readSavedLanguage() {
  if (typeof window === 'undefined') {
    return 'en';
  }
  try {
    const raw = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (raw) {
      return normalizeLanguageCode(raw);
    }
  } catch {
    // ignore quota / private mode
  }
  return 'en';
}

export function applyDocumentDirection(lng) {
  if (typeof document === 'undefined') {
    return;
  }
  const code = normalizeLanguageCode(lng);
  const isRTL = code === 'ar';
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  document.documentElement.lang = code;
}

export async function changeAppLanguage(lng) {
  const nextLanguage = normalizeLanguageCode(lng);
  const currentLanguage = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language);

  if (nextLanguage === currentLanguage) {
    return;
  }

  const { setIsChangingLanguage } = useLanguageTransitionStore.getState();
  setIsChangingLanguage(true);

  try {
    await i18n.changeLanguage(nextLanguage);
    if (typeof window !== 'undefined') {
      await new Promise(resolve => requestAnimationFrame(() => resolve()));
    }
  } finally {
    setIsChangingLanguage(false);
  }
}

const savedLanguage = readSavedLanguage();

applyDocumentDirection(savedLanguage);

i18n.use(initReactI18next).init({
  resources: {
    en: { ...inPerformanceResources.en },
    ar: { ...inPerformanceResources.ar },
  },
  lng: savedLanguage,
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGUAGES,
  ns: [...inPerformanceNamespaces],
  defaultNS: 'camera-permission',
  interpolation: {
    escapeValue: false,
  },
});

i18n.on('languageChanged', lng => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    }
  } catch {
    // ignore
  }
  applyDocumentDirection(lng);
});

export default i18n;
